import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Stack,
  Text,
  Image,
  VStack,
  HStack,
  SimpleGrid,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // 상태 추가
  const [trackCount, setTrackCount] = useState(null);
  const [creatorCount, setCreatorCount] = useState(null);
  const [noAccidentDays, setNoAccidentDays] = useState(null);

  // 토큰 갱신 함수
  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        throw new Error("No refresh token");
      }

      const response = await fetch(`https://plpl.rhoonart.co.kr/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const result = await response.json();
      if (result?.data?.access_token) {
        localStorage.setItem("access_token", result.data.access_token);
        return result.data.access_token;
      } else {
        // refresh_token도 만료/실패 시
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.href = "/";
        throw new Error("Failed to refresh token");
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      // refresh token도 만료된 경우 홈으로 이동
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      window.location.href = "/";
      throw error;
    }
  };

  useEffect(() => {
    const fetchAndStoreTokens = async () => {
      // 로그인된 사용자가 아니면 토큰 발급 시도하지 않음
      if (!user) return;

      try {
        // 1. user 테이블에서 id 조회
        const { data, error } = await supabase
          .from("user")
          .select("id")
          .eq("email", user.email)
          .single();

        if (error || !data?.id) return;
        const uniqueId = data.id;

        // 2. 토큰 요청
        const response = await fetch(`https://plpl.rhoonart.co.kr/auth/token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId: uniqueId }),
        });

        const result = await response.json();
        if (result?.data?.access_token && result?.data?.refresh_token) {
          localStorage.setItem("access_token", result.data.access_token);
          localStorage.setItem("refresh_token", result.data.refresh_token);
        }
      } catch (e) {
        console.error("Token fetch failed:", e);
      }
    };
    fetchAndStoreTokens();

    // 1. track 테이블 row 개수
    const fetchTrackCount = async () => {
      const { count } = await supabase
        .from("track")
        .select("*", { count: "exact", head: true });
      setTrackCount(count);
    };

    // 2. shorts_contract 테이블 row 개수
    const fetchCreatorCount = async () => {
      const { count } = await supabase
        .from("shorts_contract")
        .select("*", { count: "exact", head: true });
      setCreatorCount(count);
    };

    // 3. 정산 무사고 기록 날짜 계산
    const startDate = new Date(2017, 10, 29); // 11월=10 (0-indexed)
    const today = new Date();
    const diffTime = today.getTime() - startDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    setNoAccidentDays(diffDays);

    fetchTrackCount();
    fetchCreatorCount();
  }, []);

  return (
    <Box>
      {/* 서버/DB 이전 안내 오버레이 */}
      {/* <Box
        position="fixed"
        top={0}
        left={0}
        w="100vw"
        h="100vh"
        bg="rgba(215, 215, 215, 0.90)"
        zIndex={9999}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          bg="white"
          px={{ base: 6, md: 16 }}
          py={{ base: 8, md: 12 }}
          borderRadius="2xl"
          boxShadow="2xl"
          textAlign="center"
          maxW="90vw"
        >
          <Text
            fontSize={{ base: "lg", md: "2xl" }}
            fontWeight="bold"
            color="blue.500"
            mb={4}
          >
            현재 서버 및 데이터베이스 이전으로
            <br />
            플플 및 레이블리 서비스 이용이 어렵습니다.
          </Text>
          <Text fontSize={{ base: "md", md: "xl" }} color="gray.700" mb={2}>
            서비스 이전 (예정) 완료 시점 : <b>5월 16일 (금) 14:00</b>
          </Text>
          <Text fontSize={{ base: "sm", md: "md" }} color="gray.500">
            이전 작업에 따라 완료 시점이 변동될 수 있습니다.
          </Text>
        </Box>
      </Box> */}

      {/* 히어로 섹션 */}
      <Box
        bgGradient="linear(to-br, #2563eb, #60a5fa 80%)"
        color="white"
        py={{ base: 24, md: 32 }}
      >
        <Container maxW="container.xl">
          <Flex
            direction={{ base: "column", md: "row" }}
            align="center"
            justify="space-between"
            gap={{ base: 12, md: 24 }}
          >
            <Box flex={{ base: 1, md: 1.2 }} minW={0} mb={{ base: 10, md: 0 }}>
              <Heading
                fontWeight="extrabold"
                fontSize={{ base: "2.5rem", md: "3.3rem" }}
                mb={8}
                lineHeight={1.2}
              >
                디지털 음반 발매부터,
                <br />
                영상 크리에이터와 협업까지
              </Heading>
              <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="medium">
                나의 음악을 전 세계에 발매할 수 있어요.
                <br />
                음반이 없어도, 크리에이터와 협업할 수 있어요.
              </Text>
            </Box>
            <Flex
              flex={{ base: 1, md: 1 }}
              gap={8}
              justify="center"
              minW={0}
              wrap="wrap"
            >
              <Image
                src="./images/home-title1.png"
                boxSize={{ base: "140px", md: "220px" }}
                borderRadius="2xl"
              />
              <Image
                src="/images/home-title2.png"
                boxSize={{ base: "140px", md: "220px" }}
                borderRadius="2xl"
              />
              <Image
                src="/images/home-title3.png"
                boxSize={{ base: "140px", md: "220px" }}
                borderRadius="2xl"
              />
            </Flex>
          </Flex>
        </Container>
      </Box>

      {/* 통계 섹션 */}
      <Box bg="#f5f7fa" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
            <Box
              bg="white"
              p={{ base: 8, md: 12 }}
              borderRadius="2xl"
              boxShadow="xl"
              textAlign="center"
            >
              <Image
                src="/images/home-logo2.JPG"
                boxSize={{ base: "90px", md: "120px" }}
                borderRadius="lg"
                mb={4}
                mx="auto"
              />
              <Heading size="md" mb={2} fontWeight="bold">
                발매 된 음반
              </Heading>
              <Text
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="extrabold"
                mb={1}
              >
                {trackCount !== null ? (
                  `${trackCount.toLocaleString()}개`
                ) : (
                  <span>...</span>
                )}
              </Text>
              <Text color="gray.500" fontSize="sm">
                2018년부터 발매 된 누적 트랙 기준
              </Text>
            </Box>
            <Box
              bg="white"
              p={{ base: 8, md: 12 }}
              borderRadius="2xl"
              boxShadow="xl"
              textAlign="center"
            >
              <Image
                src="/images/home-logo4.png"
                boxSize={{ base: "90px", md: "120px" }}
                borderRadius="lg"
                mb={4}
                mx="auto"
              />
              <Heading size="md" mb={2} fontWeight="bold">
                메가 크리에이터의 수
              </Heading>
              <Text
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="extrabold"
                mb={1}
              >
                {creatorCount !== null ? (
                  `${creatorCount.toLocaleString()}명`
                ) : (
                  <span>...</span>
                )}
              </Text>
              <Text color="gray.500" fontSize="sm">
                월 조회수 1,000만회가 넘는 크리에이터
              </Text>
            </Box>
            <Box
              bg="white"
              p={{ base: 8, md: 12 }}
              borderRadius="2xl"
              boxShadow="xl"
              textAlign="center"
            >
              <Image
                src="/images/home-logo5.png"
                boxSize={{ base: "90px", md: "120px" }}
                borderRadius="lg"
                mb={4}
                mx="auto"
              />
              <Heading size="md" mb={2} fontWeight="bold">
                정산 무사고 기록
              </Heading>
              <Text
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="extrabold"
                mb={1}
              >
                {noAccidentDays !== null ? (
                  `${noAccidentDays.toLocaleString()}일`
                ) : (
                  <span>...</span>
                )}
              </Text>
              <Text color="gray.500" fontSize="sm">
                법인 설립 후 모든 수익 정상 지급
              </Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CID 및 쇼핑 음원 섹션 */}
      <Box bg="#f5f7fa" pb={0}>
        <Box bg="white" py={{ base: 16, md: 24 }}>
          <Container maxW="container.xl">
            <Flex
              direction={{ base: "column", md: "row" }}
              align="center"
              justify="center"
              gap={16}
            >
              <Box textAlign={{ base: "center", md: "left" }} flex={1}>
                <Text
                  fontSize={{ base: "md", md: "xl" }}
                  color="blue.500"
                  fontWeight="bold"
                  mb={3}
                >
                  유튜브 CID와 쇼핑 음원
                </Text>
                <Heading size="xl" mb={4} fontWeight="extrabold">
                  유튜브 CID와 쇼핑 음원 등록,
                  <br />
                  <Box as="span" color="blue.600">
                    플플에서는 모두에게 제공해요
                  </Box>
                </Heading>
                <Text color="gray.600" fontSize={{ base: "md", md: "xl" }}>
                  음반 발매, 음원 협업 등에 제공되는 모든 음악의 CID 및 쇼핑
                  음원 등록
                  <br />
                  #유튜브쇼핑음원 #CID
                </Text>
              </Box>
              <Image
                src="/images/play-button.png"
                boxSize={{ base: "250px", md: "250px" }}
                borderRadius="lg"
              />
            </Flex>
          </Container>
        </Box>
        <Box bg="#f5f9fa" py={{ base: 16, md: 24 }}>
          <Container maxW="container.xl">
            <Flex
              direction={{ base: "column", md: "row-reverse" }}
              align="center"
              justify="center"
              gap={16}
            >
              <Box textAlign={{ base: "center", md: "right" }} flex={1}>
                <Text
                  fontSize={{ base: "md", md: "xl" }}
                  color="blue.500"
                  fontWeight="bold"
                  mb={3}
                >
                  가장 빠른 발매 스케줄
                </Text>
                <Heading size="xl" mb={4} fontWeight="extrabold">
                  국내에서 가장 빠른 음반 발매 스케줄
                  <br />
                  <Box as="span" color="blue.600">
                    플플에서만 제공해요
                  </Box>
                </Heading>
                <Text color="gray.600" fontSize={{ base: "md", md: "xl" }}>
                  전 세계에 가장 빠르게 음반 발매
                </Text>
              </Box>
              <Image
                src="/images/note.png"
                boxSize={{ base: "250px", md: "250px" }}
                borderRadius="lg"
              />
            </Flex>
          </Container>
        </Box>
        <Box bg="white" py={{ base: 16, md: 24 }}>
          <Container maxW="container.xl">
            <Flex
              direction={{ base: "column", md: "row" }}
              align="center"
              justify="center"
              gap={16}
            >
              <Box textAlign={{ base: "center", md: "left" }} flex={1}>
                <Text
                  fontSize={{ base: "md", md: "xl" }}
                  color="blue.500"
                  fontWeight="bold"
                  mb={3}
                >
                  권리자의 권리, 안전하게
                </Text>
                <Heading size="xl" mb={4} fontWeight="extrabold">
                  내가 제작한 나의 음악
                  <br />
                  소중하고 안전하게 관리
                </Heading>
                <Text color="gray.600" fontSize={{ base: "md", md: "xl" }}>
                  AWS 서버에 안전하게 보관 및 보호
                </Text>
              </Box>
              <Image
                src="/images/collaboration/star-badge.png"
                boxSize={{ base: "250px", md: "250px" }}
                borderRadius="lg"
              />
            </Flex>
          </Container>
        </Box>
      </Box>

      {/* 안전하고 투명한 정산 섹션 */}
      <Box bg="#f5f9fa" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <Text
            textAlign="center"
            color="gray.700"
            fontSize={{ base: "md", md: "xl" }}
            mb={2}
          >
            법인 설립일 2017년 11월 29일부터,
            <br />
            정산 무사고{" "}
            <Box as="span" color="blue.500" fontWeight="bold" display="inline">
              2,708일
            </Box>
            째
          </Text>
          <Heading
            as="h2"
            size="2xl"
            textAlign="center"
            mb={12}
            mt={2}
            fontWeight="extrabold"
          >
            안전하고 투명한 정산
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            {/* 2019년 소*바다 사태 카드 */}
            <Box
              bg="#181b1e"
              color="white"
              borderRadius="2xl"
              p={{ base: 8, md: 12 }}
              boxShadow="2xl"
              minH="420px"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box>
                <Text fontSize="md" color="gray.200" mb={2} fontWeight="medium">
                  경영악화로 인한 음원 수익 미정산
                </Text>
                <Heading as="h3" size="lg" mb={3} fontWeight="bold">
                  2019년 소*바다 사태
                </Heading>
                <Text fontSize="md" mb={6} lineHeight={1.7}>
                  플플을 운영하는 (주)루나르트는 권리자 권리와 이익 보호를 위해
                  <br />
                  (주)소*리바다의 수익 미지급분을 (주)루나르트의 비용으로
                  지급하여
                  <br />
                  (주)루나르트에 음반을 발매한 권리자를 보호
                </Text>
              </Box>
              <Image
                src="/images/settle-star.png"
                boxSize={{ base: "300px", md: "300px" }}
                mx="auto"
                mt={4}
                marginTop="40px"
                marginBottom="40px"
              />
            </Box>
            {/* 2024년 유통사 수익 미지급 사건 카드 */}
            <Box
              bg="white"
              color="black"
              borderRadius="2xl"
              p={{ base: 8, md: 12 }}
              boxShadow="2xl"
              minH="420px"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box>
                <Text fontSize="md" color="gray.600" mb={2} fontWeight="medium">
                  불법콘텐츠, 짜집기로 인한 수익 미정산
                </Text>
                <Heading as="h3" size="lg" mb={3} fontWeight="bold">
                  2024년 유통사 수익 미지급 사건
                </Heading>
                <Text fontSize="md" mb={6} lineHeight={1.7}>
                  특정 유통사에서 불법콘텐츠와 짜집기를 통한 음원 수익화 금지가
                  <br />
                  사전에 고지되지 않았음에도 크리에이터에게 수익이 지급되지 않는
                  <br />
                  사례가 발생하였음에도 크리에이터에게 (주)루나르트의 비용으로
                  <br />
                  수익을 지급하여 크리에이터의 이익 보호
                </Text>
              </Box>
              <Image
                src="/images/business-icon.png"
                boxSize={{ base: "300px", md: "300px" }}
                mx="auto"
                mt={4}
                marginTop="40px"
                marginBottom="40px"
              />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* 하단 CTA 섹션 */}
      <Box bg="white" py={16}>
        <Container maxW="container.xl">
          <Box bg="white" p={{ base: 8, md: 16 }}>
            <Stack spacing={16}>
              {/* 첫 번째 행: 좌측 설명 + 오른쪽 끝 이미지 */}
              <Flex
                direction={{ base: "column", md: "row" }}
                align="center"
                justify="space-between"
                gap={8}
              >
                <Box textAlign={{ base: "center", md: "left" }} flex={1}>
                  <Text fontSize="md" color="blue.500" fontWeight="bold" mb={2}>
                    음반 발매
                  </Text>
                  <Heading
                    as="h3"
                    size="lg"
                    mb={4}
                    color="black"
                    fontWeight="extrabold"
                  >
                    한번 등록으로,
                    <br />
                    나의 음악을
                    <br />전 세계 음악/영상 플랫폼에 등록
                    <br />
                    <Box as="span" color="purple.600" fontWeight="bold">
                      플플
                    </Box>
                  </Heading>
                </Box>
                <Image
                  src="/images/plpl.png"
                  boxSize="300px"
                  borderRadius="full"
                  alt="플플 로고"
                  mb={{ base: 4, md: 0 }}
                />
              </Flex>
              {/* 두 번째 행: 좌측 끝 이미지 + 우측 설명 */}
              <Flex
                direction={{ base: "column", md: "row" }}
                align="center"
                justify="space-between"
                gap={8}
              >
                <Image
                  src="/images/laeebly.png"
                  boxSize="300px"
                  display="inline-block"
                  mb={{ base: 4, md: 0 }}
                />
                <Box textAlign={{ base: "center", md: "right" }} flex={1}>
                  <Text fontSize="md" color="blue.500" fontWeight="bold" mb={2}>
                    음원 협업
                  </Text>
                  <Heading
                    as="h3"
                    size="lg"
                    mb={2}
                    color="black"
                    fontWeight="extrabold"
                  >
                    나의 음악이 없어도
                    <br />
                    메가 크리에이터라면
                    <br />
                    음원 협업 서비스 제공
                    <br />
                    <Box as="span" color="#2ec4b6" fontWeight="bold">
                      레이블리 for 쇼츠
                    </Box>
                  </Heading>
                </Box>
              </Flex>
              {/* 하단: CTA 버튼 중앙 정렬 */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={4}
                mt={4}
              >
                <Button
                  colorScheme="blue"
                  size="lg"
                  px={10}
                  onClick={() => {
                    if (!user) {
                      navigate(
                        `/login?redirect=${encodeURIComponent("/release")}`
                      );
                    } else {
                      navigate("/release");
                    }
                  }}
                  boxShadow="md"
                  borderRadius="xl"
                  fontWeight="bold"
                >
                  음반 발매하기
                </Button>
                <Button
                  bg="gray.700"
                  color="white"
                  size="lg"
                  px={10}
                  _hover={{ bg: "gray.800" }}
                  onClick={() => {
                    if (!user) {
                      navigate(
                        `/login?redirect=${encodeURIComponent(
                          "/laeeblyforshorts"
                        )}`
                      );
                    } else {
                      navigate("/laeeblyforshorts");
                      setTimeout(() => window.scrollTo(0, 0), 0);
                    }
                  }}
                  boxShadow="md"
                  borderRadius="xl"
                  fontWeight="bold"
                >
                  음원 협업
                </Button>
              </Box>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* 정부지원/투자 섹션 */}
      <Box
        bgGradient="linear(to-br, #2196f3, #0d8ddb 80%)"
        py={{ base: 16, md: 28 }}
      >
        <Container maxW="container.xl">
          <Text
            fontSize={{ base: "xl", md: "3xl" }}
            color="white"
            fontWeight="bold"
            textAlign="center"
            mb={{ base: 8, md: 16 }}
          >
            플플과 레이블리는 디지털 음원 유통 혁신 기업 (주)루나르트가
            운영합니다.
          </Text>
          <Box
            bg="white"
            borderRadius="3xl"
            p={{ base: 6, md: 16 }}
            boxShadow="2xl"
            maxW="6xl"
            mx="auto"
          >
            <Text fontSize={{ base: "md", md: "xl" }} fontWeight="bold" mb={4}>
              다수의 투자 유치와 정부지원 프로그램에 선정
            </Text>
            <Text fontSize={{ base: "sm", md: "lg" }} mb={6} color="gray.700">
              (주)루나르트의 서비스는 CNTTech, 한양대학교 기술지주회사로부터
              투자를 받았으며, 문화체육관광부 산하 한국콘텐츠진흥원과
              예술경영지원센터로부터 누적 8억원, 중소벤처기업부 TIPS 프로그램
              통해 7억원의 지원받아 개발 및 운영되고 있습니다.
            </Text>
            <Box
              as="ul"
              fontSize={{ base: "xs", md: "md" }}
              color="gray.700"
              mb={8}
              pl={4}
            >
              <li>
                <a
                  href="https://www.thebell.co.kr/free/content/ArticleView.asp?key=202408121442216520101288"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  2024-08-14 [씨엔티테크 액셀러레이팅 스토리] 음원 연결 플랫폼
                  '루나르트', 숏폼으로 영토 확장 시동
                </a>
              </li>
              <li>
                <a
                  href="https://www.newswire.co.kr/newsRead.php?no=989654"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  2024-05-13 킹슬리벤처스, 2024 예술분야 창업도약 지원사업
                  '킹스맨 트리니티' 선정 워크숍 진행···예술분야 창업기업 육성
                  시작
                </a>
              </li>
              <li>
                <a
                  href="https://www.newsis.com/view/?id=NISX20231031_0002503485&cID=10701&pID=10700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  2023-10-31 문화예술 스타트업 키운다···문체부-예경 '예술로
                  데모데이'
                </a>
              </li>
              <li>
                <a
                  href="https://www.newsis.com/view/?id=NISX20231031_0002503485&cID=10701&pID=10700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  2022-11-25 [Pangyo Tech] 경기창조경제혁신센터, '제9회 스타트업
                  815' 개최··· "아트테크 분야 기업 대상 맞춤형 트랙 운영!"
                </a>
              </li>
              <li>
                <a
                  href="https://www.donga.com/news/article/all/20211130/110536436/1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  2021-12-01 "혁신 콘텐츠 창업자에 세계시장 도약 날개
                  달아드립니다"
                </a>
              </li>
            </Box>
            <SimpleGrid
              columns={{ base: 3, md: 3 }}
              spacingY={20}
              spacingX={10}
              alignItems="center"
              justifyItems="center"
            >
              <Image
                src="/images/support/cnttech.png"
                h={{ base: "44px", md: "70px" }}
                objectFit="contain"
                alt="CNTTech"
              />
              <Image
                src="/images/support/hyuholdings.png"
                h={{ base: "40px", md: "60px" }}
                objectFit="contain"
                alt="HYU Holdings"
              />
              <Image
                src="/images/support/mss.png"
                h={{ base: "40px", md: "60px" }}
                objectFit="contain"
                alt="중소벤처기업부"
              />
              <Image
                src="/images/support/kcca.png"
                h={{ base: "40px", md: "55px" }}
                objectFit="contain"
                alt="한국콘텐츠진흥원"
              />
              <Image
                src="/images/support/kams.png"
                h={{ base: "40px", md: "60px" }}
                objectFit="contain"
                alt="예술경영지원센터"
              />
              <Image
                src="/images/support/tips.png"
                h={{ base: "40px", md: "62px" }}
                objectFit="contain"
                alt="TIPS"
              />
            </SimpleGrid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
